/**
 * Paliacate Studio - Editorial Shared Components
 * Style inspired by forthepeople.agency
 * 
 * Design principles:
 * - Movement is narrative, not decoration
 * - Scroll is the director - sections reveal progressively
 * - Custom cursor with contextual behavior
 * - Navigation always visible but never invasive
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

// Brand colors - used sparingly
export const brandColors = {
  orange: '#E66115',
  red: '#BB0B24',
  black: '#0a0a0a',
  white: '#ffffff',
};

// Custom cursor with contextual text - enhanced version
export const CustomCursor = () => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [cursorText, setCursorText] = useState('');
  const [isHovering, setIsHovering] = useState(false);
  const [cursorVariant, setCursorVariant] = useState('default'); // default, link, project, action

  useEffect(() => {
    const handleMouseMove = (e) => {
      setPosition({ x: e.clientX, y: e.clientY });
    };

    const handleMouseOver = (e) => {
      const target = e.target.closest('[data-cursor]');
      if (target) {
        const text = target.dataset.cursor;
        setCursorText(text);
        setIsHovering(true);
        
        // Determine cursor variant based on context
        if (text.includes('proyecto') || text.includes('ver')) {
          setCursorVariant('project');
        } else if (text.includes('escribir') || text.includes('contacto')) {
          setCursorVariant('action');
        } else {
          setCursorVariant('link');
        }
      }
    };

    const handleMouseOut = (e) => {
      const target = e.target.closest('[data-cursor]');
      if (target) {
        setCursorText('');
        setIsHovering(false);
        setCursorVariant('default');
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', handleMouseOut);
    };
  }, []);

  // Dynamic cursor size based on variant
  const getCursorSize = () => {
    if (!isHovering) return 12;
    if (cursorVariant === 'project') return 100;
    if (cursorVariant === 'action') return 90;
    return cursorText.length > 10 ? 100 : 80;
  };

  const cursorSize = getCursorSize();

  return (
    <motion.div
      className="fixed top-0 left-0 pointer-events-none z-[9999] mix-blend-difference hidden lg:flex items-center justify-center"
      animate={{
        x: position.x - cursorSize / 2,
        y: position.y - cursorSize / 2,
        width: cursorSize,
        height: cursorSize,
      }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
    >
      <motion.div 
        className="rounded-full bg-white"
        animate={{
          width: isHovering ? '100%' : 12,
          height: isHovering ? '100%' : 12,
        }}
        transition={{ duration: 0.2 }}
      />
      <AnimatePresence>
        {isHovering && cursorText && (
          <motion.span 
            className="absolute text-black text-[10px] font-medium uppercase tracking-wider text-center px-2"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.15 }}
          >
            {cursorText}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Navigation menu items
const menuItems = [
  { label: 'Manifiesto', href: '/manifiesto' },
  { label: 'Cómo trabajamos', href: '/metodo' },
  { label: 'Nuestro trabajo', href: '/casos' },
  { label: 'Contacto', href: '/contacto' },
];

// Minimal floating navigation - PALIACATE left, MENU right (fullscreen on click)
export const EditorialNav = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <>
      <motion.nav
        className={`fixed top-0 left-0 right-0 z-50 px-6 py-5 flex justify-between items-center transition-all duration-500 ${
          scrolled ? 'bg-[#0a0a0a]/90 backdrop-blur-md' : ''
        }`}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.8 }}
      >
        <Link 
          to="/" 
          className="text-white text-sm uppercase tracking-[0.15em] font-medium hover:opacity-60 transition-opacity"
          data-cursor="inicio"
        >
          Paliacate
        </Link>
        
        {/* Menu button - same for desktop and mobile */}
        <button
          onClick={() => setIsOpen(true)}
          className="text-white text-sm uppercase tracking-[0.15em] hover:opacity-60 transition-opacity"
          data-cursor="menu"
        >
          Menu
        </button>
      </motion.nav>

      {/* Fullscreen menu - opens on both desktop and mobile */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 z-[100] bg-[#0a0a0a] flex flex-col justify-center items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-5 right-6 text-white text-sm uppercase tracking-[0.15em] hover:opacity-60 transition-opacity"
              data-cursor="cerrar"
            >
              Cerrar
            </button>
            
            <nav className="flex flex-col items-center gap-4 md:gap-6">
              {[
                { label: 'Inicio', href: '/' },
                ...menuItems,
                { label: 'Servicios', href: '/servicios' },
                { label: 'Equipo', href: '/equipo' },
                { label: 'Arquitectura', href: '/arquitectura-paliacate' },
                { label: 'Portal Clientes', href: '/login' },
              ].map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.05 }}
                >
                  <Link
                    to={item.href}
                    onClick={() => setIsOpen(false)}
                    className="text-white text-4xl md:text-6xl font-light hover:opacity-60 transition-opacity"
                    data-cursor="ver"
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
            </nav>

            <motion.div 
              className="absolute bottom-10 flex gap-8 text-white/50 text-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {socialLinks.map((link) => (
                <a 
                  key={link.name}
                  href={link.href} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="hover:text-white transition-colors"
                >
                  {link.name}
                </a>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Social media links
export const socialLinks = [
  { name: 'Instagram', href: 'https://instagram.com/paliacatestudio' },
  { name: 'WhatsApp', href: 'https://wa.me/527774385617' },
  { name: 'Facebook', href: 'https://facebook.com/paliacatestudio' },
  { name: 'TikTok', href: 'https://tiktok.com/@paliacatestudio' },
];

// Logo Reveal Footer - reveals and grows on scroll
export const LogoRevealFooter = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end end"]
  });
  
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.3, 0.7, 1]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [0, 1]);
  const linksOpacity = useTransform(scrollYProgress, [0.5, 0.8], [0, 1]);
  const linksY = useTransform(scrollYProgress, [0.5, 0.8], [30, 0]);

  return (
    <footer 
      ref={containerRef} 
      className="min-h-screen bg-[#0a0a0a] flex flex-col justify-center items-center relative px-6 py-20"
    >
      {/* Large logo that reveals on scroll */}
      <motion.div 
        className="text-center mb-16"
        style={{ scale, opacity }}
      >
        <h2 className="text-white text-[18vw] md:text-[14vw] font-extralight leading-none tracking-tight uppercase">
          Paliacate
        </h2>
      </motion.div>

      {/* Navigation links */}
      <motion.div 
        className="w-full max-w-4xl"
        style={{ opacity: linksOpacity, y: linksY }}
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12 text-center md:text-left">
          <div className="space-y-3">
            <Link to="/manifiesto" className="text-white hover:opacity-60 transition-opacity block" data-cursor="ver">Manifiesto</Link>
            <Link to="/metodo" className="text-white hover:opacity-60 transition-opacity block" data-cursor="ver">Cómo trabajamos</Link>
            <Link to="/casos" className="text-white hover:opacity-60 transition-opacity block" data-cursor="ver">Nuestro trabajo</Link>
            <Link to="/contacto" className="text-white hover:opacity-60 transition-opacity block" data-cursor="escribir">Contacto</Link>
          </div>
          <div className="space-y-3">
            <Link to="/servicios" className="text-white hover:opacity-60 transition-opacity block" data-cursor="ver">Servicios</Link>
            <Link to="/arquitectura-paliacate" className="text-white hover:opacity-60 transition-opacity block" data-cursor="ver">Arquitectura</Link>
            <Link to="/equipo" className="text-white hover:opacity-60 transition-opacity block" data-cursor="ver">Equipo</Link>
            <Link to="/login" className="text-white hover:opacity-60 transition-opacity block" data-cursor="entrar">Portal Clientes</Link>
          </div>
          <div className="space-y-3">
            <Link to="/privacidad" className="text-white/50 hover:text-white transition-colors block">Privacidad</Link>
            <Link to="/terminos" className="text-white/50 hover:text-white transition-colors block">Términos</Link>
          </div>
          <div className="space-y-3">
            {socialLinks.map((link) => (
              <a 
                key={link.name}
                href={link.href} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-white/50 hover:text-white transition-colors block"
                data-cursor={link.name.toLowerCase()}
              >
                {link.name}
              </a>
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <span className="text-white/30 text-sm">
            Diseñado y construido por Paliacate © {new Date().getFullYear()}
          </span>
          <a 
            href="mailto:hola@paliacatestudio.com" 
            className="text-white/50 hover:text-white transition-colors text-sm"
            data-cursor="email"
          >
            hola@paliacatestudio.com
          </a>
        </div>
      </motion.div>
    </footer>
  );
};

// Simple footer for legal pages
export const SimpleFooter = () => {
  return (
    <footer className="bg-[#0a0a0a] border-t border-white/10 py-8 px-6">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
        <span className="text-white/30 text-sm">
          © {new Date().getFullYear()} Paliacate Studio
        </span>
        <div className="flex gap-6 text-white/30 text-sm">
          <Link to="/privacidad" className="hover:text-white transition-colors">Privacidad</Link>
          <Link to="/terminos" className="hover:text-white transition-colors">Términos</Link>
        </div>
      </div>
    </footer>
  );
};

// Global styles with smooth scroll
export const EditorialStyles = () => (
  <style>{`
    * { cursor: none; }
    @media (max-width: 1024px) { * { cursor: auto; } }
    
    html { 
      scroll-behavior: smooth; 
    }
    
    body { 
      background: #0a0a0a;
      overflow-x: hidden;
    }
    
    ::selection {
      background: rgba(255, 255, 255, 0.2);
      color: white;
    }
    
    /* Smooth scrolling for all */
    * {
      scroll-behavior: smooth;
    }
  `}</style>
);

// Page wrapper component
export const PageWrapper = ({ children, showLogoFooter = true }) => {
  return (
    <>
      <EditorialStyles />
      <CustomCursor />
      <EditorialNav />
      <main className="bg-[#0a0a0a] min-h-screen">
        {children}
      </main>
      {showLogoFooter ? <LogoRevealFooter /> : <SimpleFooter />}
    </>
  );
};
